'use client';

import { useAuth } from '@/contexts/AuthProvider';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useWorkspaceSocket } from '@/hooks/useWorkspaceSocket';
import { IWorkflowNotification } from '@/types/plays';
import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback, ReactNode } from 'react';
import { io } from 'socket.io-client';
import { useDebouncedCallback } from 'use-debounce';
import appLogger from '@/utils/logger';
import {
  isCompletedNotification as isCompletedNotificationHelper,
  isActiveNotification as isActiveNotificationHelper,
  filterUserFacingNotifications,
} from '@/utils/notificationHelpers';
import {
  emitAudienceSubscriptionBatch,
  removeDeniedAudienceSubscription,
  retryDeniedAudienceSubscriptionBatch,
} from '@/utils/socketAudienceSubscriptions';

// localStorage configuration
const STORAGE_PREFIX = 'mb_workflow_notifications';
const COMPLETED_NOTIFICATION_MAX_AGE_HOURS = 24; // Clear completed notifications after 24 hours
const ACTIVE_NOTIFICATION_MAX_AGE_HOURS = 72; // Keep active/running notifications for 72 hours (in case of long-running workflows)
const MAX_RECENT_COMPLETED_PER_AUDIENCE = 10; // Keep last 10 completed notifications per audience for reference
const MAX_NOTIFICATIONS_PER_AUDIENCE = 50; // Hard limit: max 50 notifications per audience
const MAX_STORAGE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB max storage size

const sanitizeKeyPart = (value: any) => String(value ?? 'undefined').replace(/\|/g, '_');

const getNotificationExecutionId = (notification: IWorkflowNotification): string | number | undefined => {
  return notification.executionId
    || notification.raw?.executionId
    || notification.raw?.execution_id
    || notification.raw?.play_execution_id
    || notification.raw?.playExecutionId;  // emailRecipientWorker uses camelCase
};

/**
 * Unique key for deduplication - includes state and timestamp
 * Used to prevent exact duplicate notifications
 */
const getNotificationKey = (notification: IWorkflowNotification): string => {
  const sessionId = (notification as any)?.session_id;
  const executionId = getNotificationExecutionId(notification);
  const audiencePlayId = notification.audiencePlayId || notification.raw?.audiencePlayId || notification.raw?.audience_play_id;
  const messageId = notification.raw?.messageId || notification.raw?.message_id;
  const recipientEmail = notification.raw?.recipientEmail || notification.raw?.recipient_email;
  const stableId = executionId || messageId || audiencePlayId || '';

  const baseParts = [
    sanitizeKeyPart(stableId || ''),
    sanitizeKeyPart(notification.worker),
    sanitizeKeyPart(notification.type),
    sanitizeKeyPart(notification.state),
    sanitizeKeyPart(notification.audienceId ?? 'unknown_audience'),
    sanitizeKeyPart(notification.touchTag || ''),
    sanitizeKeyPart(recipientEmail || ''),
    sanitizeKeyPart(sessionId || ''),
  ];

  if (!stableId) {
    baseParts.push(sanitizeKeyPart(notification.timestamp || 'unknown_timestamp'));
  }

  return baseParts.join('|');
};

/**
 * Execution key - identifies the same workflow execution across different states
 * Used to replace started/processing with completed/failed
 * Does NOT include state or timestamp, so started → completed are the same execution
 *
 * IMPORTANT: Prioritize audiencePlayId over executionId because some workers don't include
 * executionId in all their events (e.g., skipped, failed states). Using audiencePlayId ensures
 * all notifications for the same workflow run consolidate correctly.
 */
const getExecutionKey = (notification: IWorkflowNotification): string => {
  const sessionId = (notification as any)?.session_id;
  const executionId = getNotificationExecutionId(notification);
  const audiencePlayId = notification.audiencePlayId || notification.raw?.audiencePlayId || notification.raw?.audience_play_id;
  const messageId = notification.raw?.messageId || notification.raw?.message_id;
  const recipientEmail = notification.raw?.recipientEmail || notification.raw?.recipient_email;
  // Prioritize audiencePlayId for consolidation (all events in same workflow run share this)
  const stableExecId = audiencePlayId || executionId || messageId || '';
  return [
    sanitizeKeyPart(stableExecId || ''),
    sanitizeKeyPart(notification.worker),
    sanitizeKeyPart(notification.type),
    sanitizeKeyPart(notification.audienceId ?? 'unknown_audience'),
    sanitizeKeyPart(notification.touchTag || ''),
    sanitizeKeyPart(recipientEmail || ''),
    sanitizeKeyPart(sessionId || ''),
  ].join('|');
};

/**
 * Check if a notification represents a completed/final state
 * Uses centralized helper from utils/notificationHelpers.ts
 */
const isCompletedNotification = isCompletedNotificationHelper;

/**
 * Get localStorage key for notifications (workspace-scoped)
 */
const getStorageKey = (workspaceId: number | undefined): string | null => {
  if (!workspaceId) return null;
  return `${STORAGE_PREFIX}_workspace_${workspaceId}`;
};

/**
 * Get localStorage key for the "notifications seen at" timestamp (workspace-scoped).
 * Tracks when the user last opened the notifications drawer so the badge can show
 * only unseen active notifications instead of getting stuck on stale ones.
 */
const getSeenAtStorageKey = (workspaceId: number | undefined): string | null => {
  if (!workspaceId) return null;
  return `${STORAGE_PREFIX}_seen_at_workspace_${workspaceId}`;
};

/**
 * Load the "seen at" timestamp (epoch ms) from localStorage. 0 = never seen.
 */
const loadSeenAtFromStorage = (workspaceId: number | undefined): number => {
  if (typeof window === 'undefined') return 0;
  const storageKey = getSeenAtStorageKey(workspaceId);
  if (!storageKey) return 0;
  const raw = localStorage.getItem(storageKey);
  const parsed = raw ? Number(raw) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Load notifications from localStorage
 */
const loadNotificationsFromStorage = (workspaceId: number | undefined): IWorkflowNotification[] => {
  if (typeof window === 'undefined') return [];
  
  const storageKey = getStorageKey(workspaceId);
  if (!storageKey) return [];

  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return [];

    const parsed = JSON.parse(stored) as IWorkflowNotification[];
    appLogger.debug('[NotificationProvider] Loaded notifications from localStorage', {
      count: parsed.length,
      workspaceId,
    });
    return parsed;
  } catch (error) {
    appLogger.trackError(error as Error, {
      component: 'NotificationProvider',
      message: 'Failed to load notifications from localStorage',
      workspaceId,
    });
    return [];
  }
};

/**
 * Save notifications to localStorage
 */
const saveNotificationsToStorage = (notifications: IWorkflowNotification[], workspaceId: number | undefined): void => {
  if (typeof window === 'undefined') return;
  if (!workspaceId) return;

  const storageKey = getStorageKey(workspaceId);
  if (!storageKey) return;

  try {
    const serialized = JSON.stringify(notifications);
    const sizeBytes = new Blob([serialized]).size;

    // Check storage size limit
    if (sizeBytes > MAX_STORAGE_SIZE_BYTES) {
      appLogger.warn('[NotificationProvider] Storage size limit exceeded, cleaning up oldest notifications', {
        sizeBytes,
        maxBytes: MAX_STORAGE_SIZE_BYTES,
        workspaceId,
      });
      // Clean up oldest notifications until under limit
      const cleaned = cleanupBySize(notifications, MAX_STORAGE_SIZE_BYTES);
      localStorage.setItem(storageKey, JSON.stringify(cleaned));
      appLogger.debug('[NotificationProvider] Saved cleaned notifications to localStorage', {
        originalCount: notifications.length,
        cleanedCount: cleaned.length,
        workspaceId,
      });
      return;
    }

    localStorage.setItem(storageKey, serialized);
    appLogger.debug('[NotificationProvider] Saved notifications to localStorage', {
      count: notifications.length,
      sizeBytes,
      workspaceId,
    });
  } catch (error) {
    appLogger.trackError(error as Error, {
      component: 'NotificationProvider',
      message: 'Failed to save notifications to localStorage',
      workspaceId,
    });
  }
};

/**
 * Clean up notifications by age and state
 * - Completed notifications: Clear after COMPLETED_NOTIFICATION_MAX_AGE_HOURS
 * - Active notifications: Clear after ACTIVE_NOTIFICATION_MAX_AGE_HOURS
 */
const cleanupByAge = (notifications: IWorkflowNotification[]): IWorkflowNotification[] => {
  const now = Date.now();
  const completedMaxAgeMs = COMPLETED_NOTIFICATION_MAX_AGE_HOURS * 60 * 60 * 1000;
  const activeMaxAgeMs = ACTIVE_NOTIFICATION_MAX_AGE_HOURS * 60 * 60 * 1000;

  const filtered = notifications.filter((notification) => {
    const timestamp = new Date(notification.timestamp).getTime();
    const age = now - timestamp;
    const isCompleted = isCompletedNotification(notification);
    
    // Use different age limits based on notification state
    const maxAgeMs = isCompleted ? completedMaxAgeMs : activeMaxAgeMs;
    return age <= maxAgeMs;
  });

  if (filtered.length < notifications.length) {
    appLogger.debug('[NotificationProvider] Cleaned up notifications by age', {
      originalCount: notifications.length,
      cleanedCount: filtered.length,
      removedCount: notifications.length - filtered.length,
      completedMaxAgeHours: COMPLETED_NOTIFICATION_MAX_AGE_HOURS,
      activeMaxAgeHours: ACTIVE_NOTIFICATION_MAX_AGE_HOURS,
    });
  }

  return filtered;
};

/**
 * Clean up notifications by count with smart strategy:
 * - Keep all active/running notifications
 * - Keep only most recent MAX_RECENT_COMPLETED_PER_AUDIENCE completed notifications per audience
 * - Hard limit: MAX_NOTIFICATIONS_PER_AUDIENCE total per audience
 */
const cleanupByCount = (notifications: IWorkflowNotification[]): IWorkflowNotification[] => {
  // Group by audienceId
  const byAudience = new Map<number, IWorkflowNotification[]>();
  
  notifications.forEach((notification) => {
    const audienceId = notification.audienceId;
    if (!byAudience.has(audienceId)) {
      byAudience.set(audienceId, []);
    }
    byAudience.get(audienceId)!.push(notification);
  });

  const cleaned: IWorkflowNotification[] = [];
  let totalRemoved = 0;

  byAudience.forEach((audienceNotifications, audienceId) => {
    // Separate active and completed notifications
    const active: IWorkflowNotification[] = [];
    const completed: IWorkflowNotification[] = [];

    audienceNotifications.forEach((notification) => {
      if (isCompletedNotification(notification)) {
        completed.push(notification);
      } else {
        active.push(notification);
      }
    });

    // Sort completed by timestamp (oldest first)
    completed.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeA - timeB;
    });

    // Keep only most recent completed notifications
    const keptCompleted = completed.slice(-MAX_RECENT_COMPLETED_PER_AUDIENCE);
    
    // Combine active + kept completed
    const kept = [...active, ...keptCompleted];
    
    // Apply hard limit: if total exceeds MAX_NOTIFICATIONS_PER_AUDIENCE, keep only most recent
    let finalKept = kept;
    if (kept.length > MAX_NOTIFICATIONS_PER_AUDIENCE) {
      // Sort all by timestamp and keep most recent
      const allSorted = [...kept].sort((a, b) => {
        const timeA = new Date(a.timestamp).getTime();
        const timeB = new Date(b.timestamp).getTime();
        return timeA - timeB;
      });
      finalKept = allSorted.slice(-MAX_NOTIFICATIONS_PER_AUDIENCE);
    }

    cleaned.push(...finalKept);

    const removed = audienceNotifications.length - finalKept.length;
    if (removed > 0) {
      totalRemoved += removed;
    }
  });

  if (totalRemoved > 0) {
    appLogger.debug('[NotificationProvider] Cleaned up notifications by count', {
      originalCount: notifications.length,
      cleanedCount: cleaned.length,
      removedCount: totalRemoved,
      maxRecentCompleted: MAX_RECENT_COMPLETED_PER_AUDIENCE,
      maxPerAudience: MAX_NOTIFICATIONS_PER_AUDIENCE,
    });
  }

  return cleaned;
};

/**
 * Clean up notifications by storage size (remove oldest until under limit)
 */
const cleanupBySize = (notifications: IWorkflowNotification[], maxBytes: number): IWorkflowNotification[] => {
  // Sort by timestamp (oldest first)
  const sorted = [...notifications].sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return timeA - timeB;
  });

  // Keep removing oldest until under size limit
  let current = sorted;
  while (current.length > 0) {
    const serialized = JSON.stringify(current);
    const sizeBytes = new Blob([serialized]).size;
    
    if (sizeBytes <= maxBytes) {
      break;
    }
    
    // Remove oldest notification
    current = current.slice(1);
  }

  if (current.length < notifications.length) {
    appLogger.debug('[NotificationProvider] Cleaned up notifications by size', {
      originalCount: notifications.length,
      cleanedCount: current.length,
      removedCount: notifications.length - current.length,
      maxBytes,
    });
  }

  return current;
};

/**
 * Check if a notification is an in-progress state (not yet completed/failed)
 * Uses centralized helper from utils/notificationHelpers.ts
 */
const isInProgressNotification = isActiveNotificationHelper;

/**
 * Consolidate notifications - remove in-progress notifications that have a completed counterpart
 * This cleans up old "started" notifications when "completed" exists for the same execution
 */
const consolidateNotifications = (notifications: IWorkflowNotification[]): IWorkflowNotification[] => {
  // Group notifications by execution key
  const executionGroups = new Map<string, IWorkflowNotification[]>();

  notifications.forEach((notification) => {
    const execKey = getExecutionKey(notification);
    if (!executionGroups.has(execKey)) {
      executionGroups.set(execKey, []);
    }
    executionGroups.get(execKey)!.push(notification);
  });

  // For each group, keep only the most relevant notification
  const consolidated: IWorkflowNotification[] = [];
  let removedCount = 0;

  executionGroups.forEach((group) => {
    if (group.length === 1) {
      // Only one notification for this execution, keep it
      consolidated.push(group[0]);
    } else {
      // Multiple notifications for same execution - prioritize completed/failed over in-progress
      // Sort by timestamp descending (newest first)
      const sorted = [...group].sort((a, b) => {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });

      // Check if any are completed/failed
      const completedNotif = sorted.find(isCompletedNotification);
      if (completedNotif) {
        // Keep only the completed notification, remove in-progress ones
        consolidated.push(completedNotif);
        removedCount += group.length - 1;
      } else {
        // No completed notification - keep the most recent in-progress
        consolidated.push(sorted[0]);
        removedCount += group.length - 1;
      }
    }
  });

  if (removedCount > 0) {
    appLogger.debug('[NotificationProvider] Consolidated notifications', {
      originalCount: notifications.length,
      consolidatedCount: consolidated.length,
      removedCount,
    });
  }

  return consolidated;
};

/**
 * Merge stored notifications with new socket notifications
 * - Prevents exact duplicates by checking notification keys
 * - Replaces in-progress notifications with completed/failed for same execution
 * - Replaces older in-progress with newer in-progress (started → processing)
 * - Returns sorted notifications by timestamp ascending (oldest first)
 */
const mergeNotifications = (
  stored: IWorkflowNotification[],
  newNotifications: IWorkflowNotification[]
): IWorkflowNotification[] => {
  const storedKeys = new Set(stored.map(getNotificationKey));

  // Build execution key map for existing notifications
  // Maps execution key -> index in merged array (for replacement)
  const executionKeyToIndex = new Map<string, number>();

  // Start with stored notifications
  const merged = [...stored];

  // Build index of existing execution keys for in-progress notifications
  merged.forEach((notification, index) => {
    const execKey = getExecutionKey(notification);
    // Only track in-progress notifications for replacement
    if (isInProgressNotification(notification)) {
      executionKeyToIndex.set(execKey, index);
    }
  });

  let addedCount = 0;
  let replacedCount = 0;

  newNotifications.forEach((notification) => {
    const notifKey = getNotificationKey(notification);
    const execKey = getExecutionKey(notification);

    // Skip exact duplicates
    if (storedKeys.has(notifKey)) {
      return;
    }

    // Check if this is a completed/failed notification that should replace an in-progress one
    const existingIndex = executionKeyToIndex.get(execKey);

    if (existingIndex !== undefined && isCompletedNotification(notification)) {
      // Replace the in-progress notification with the completed one
      const oldNotif = merged[existingIndex];
      appLogger.debug('[NotificationProvider] Replacing in-progress notification with completed', {
        execKey,
        oldState: oldNotif.state,
        newState: notification.state,
        worker: notification.worker,
      });
      merged[existingIndex] = notification;
      storedKeys.add(notifKey);
      executionKeyToIndex.delete(execKey); // Remove from tracking
      replacedCount++;
    } else if (existingIndex !== undefined && isInProgressNotification(notification)) {
      // Replace started with processing (if processing comes later)
      const oldNotif = merged[existingIndex];
      const oldTime = new Date(oldNotif.timestamp).getTime();
      const newTime = new Date(notification.timestamp).getTime();

      if (newTime > oldTime) {
        // Newer in-progress state replaces older one (e.g., processing replaces started)
        appLogger.debug('[NotificationProvider] Replacing older in-progress notification', {
          execKey,
          oldState: oldNotif.state,
          newState: notification.state,
          worker: notification.worker,
        });
        merged[existingIndex] = notification;
        storedKeys.add(notifKey);
        replacedCount++;
      }
    } else {
      // New notification - add it
      merged.push(notification);
      storedKeys.add(notifKey);
      addedCount++;

      // Track if it's in-progress for future replacement
      if (isInProgressNotification(notification)) {
        executionKeyToIndex.set(execKey, merged.length - 1);
      }
    }
  });

  if (addedCount > 0 || replacedCount > 0) {
    appLogger.debug('[NotificationProvider] Merged notifications', {
      storedCount: stored.length,
      newCount: newNotifications.length,
      mergedCount: merged.length,
      addedCount,
      replacedCount,
    });
  }

  // Sort by timestamp ascending (oldest first)
  return merged.sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return timeA - timeB;
  });
};

/**
 * Clean up old completed notifications when a new workflow execution starts
 * This prevents clutter - when user starts a new run, clear old completed notifications for that audience
 */
const cleanupOldCompletedOnNewExecution = (
  notifications: IWorkflowNotification[],
  newNotification: IWorkflowNotification
): IWorkflowNotification[] => {
  // Only cleanup if this is a "starting" notification (not completed)
  if (isCompletedNotification(newNotification)) {
    return notifications;
  }

  const audienceId = newNotification.audienceId;
  
  // Find all completed notifications for this audience
  const audienceNotifications = notifications.filter((n) => n.audienceId === audienceId);
  const completedForAudience = audienceNotifications.filter(isCompletedNotification);
  
  // If there are old completed notifications and a new execution is starting, clear them
  // But keep the most recent MAX_RECENT_COMPLETED_PER_AUDIENCE for reference
  if (completedForAudience.length > MAX_RECENT_COMPLETED_PER_AUDIENCE) {
    // Sort completed by timestamp (oldest first)
    const sortedCompleted = [...completedForAudience].sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeA - timeB;
    });

    // Keep only most recent completed
    const keptCompleted = sortedCompleted.slice(-MAX_RECENT_COMPLETED_PER_AUDIENCE);
    
    // Remove old completed notifications
    const keptKeys = new Set(keptCompleted.map(getNotificationKey));
    const filtered = notifications.filter((n) => {
      if (n.audienceId === audienceId && isCompletedNotification(n)) {
        return keptKeys.has(getNotificationKey(n));
      }
      return true;
    });

    if (filtered.length < notifications.length) {
      appLogger.debug('[NotificationProvider] Cleaned up old completed notifications on new execution', {
        audienceId,
        removedCount: notifications.length - filtered.length,
        keptCount: keptCompleted.length,
      });
    }

    return filtered;
  }

  return notifications;
};

/**
 * Comprehensive cleanup: consolidate, age, count, and size
 * Consolidation merges state transitions (started → completed) for the same execution
 * to keep storage clean while showing proper state progression.
 */
const cleanupNotifications = (notifications: IWorkflowNotification[]): IWorkflowNotification[] => {
  let cleaned = notifications;

  // Step 0: Consolidate - remove in-progress notifications that have completed counterparts
  cleaned = consolidateNotifications(cleaned);

  // Step 1: Clean by age (different limits for completed vs active)
  cleaned = cleanupByAge(cleaned);

  // Step 2: Clean by count per audience (smart: keep active, limit completed)
  cleaned = cleanupByCount(cleaned);

  // Step 3: Clean by size if still too large
  const serialized = JSON.stringify(cleaned);
  const sizeBytes = new Blob([serialized]).size;
  if (sizeBytes > MAX_STORAGE_SIZE_BYTES) {
    cleaned = cleanupBySize(cleaned, MAX_STORAGE_SIZE_BYTES);
  }

  return cleaned;
};

// Initialize Socket.IO instance (shared globally)
const socketUrl = process.env.NEXT_PUBLIC_SOCKET_URL!;
// Normalize path: strip leading slashes from env var, then ensure exactly one leading slash
// This handles both "/clients/..." and "clients/..." formats in env vars
const rawPath = process.env.NEXT_PUBLIC_SOCKET_IO_HUB_PATH || 'clients/socketio/hubs/Hub';
const socketPath = '/' + rawPath.replace(/^\/+/, '');
console.log('[Socket.IO] Initializing with URL:', socketUrl, 'Path:', socketPath);

export const socketIo = io(socketUrl, {
  path: socketPath,
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 2000,
  reconnectionDelayMax: 10000,
  randomizationFactor: 0.5,
  autoConnect: false,
});

/**
 * Dialer session data for Human Dialer workflows
 */
interface DialerSessionData {
  session_id: string;
  conference_name: string;
  audienceId: number;
  timestamp: string;
}

/**
 * Persona update event payload from backend personaUpdatesConsumer
 * Emitted on 'persona_update' event to persona:{personaId} and workspace_{vendorid}_updates channels
 */
export interface PersonaUpdateEvent {
  event: 'content_generated';
  personaId: string;
  audienceIds: number[];
  completedAt: string;
  timestamp: string;
}

type PersonaUpdateCallback = (data: PersonaUpdateEvent) => void;

/**
 * Persona finder result event payload from backend personaUpdatesConsumer.
 * Emitted on 'persona_finder_result' event to persona_finder:{requestId} room.
 */
export interface PersonaFinderResultEvent {
  requestId: string;
  event: 'persona_found' | 'persona_finder_error' | 'persona_pending';
  vendorid: number;
  result?: {
    people: Array<{
      first_name: string;
      last_name: string;
      title: string;
      company_name: string;
      company_domain: string;
      confidence_score: number;
      confidence_reasoning: string;
      sources: string[];
      email?: string | null;
      email_meta?: Record<string, unknown>;
      linkedin_url?: string | null;
      linkedin_meta?: Record<string, unknown>;
      phone?: string | null;
      phone_meta?: Record<string, unknown>;
    }>;
    metrics?: {
      duration_ms: number;
      credits_used?: number;
    };
  };
  error?: string;
  /** Machine-readable failure class from the worker (e.g. LUSHA_AUTH_FAILED); see lib/enrichmentErrors. */
  errorCode?: string | null;
  completedAt: string;
}

type PersonaFinderCallback = (data: PersonaFinderResultEvent) => void;

interface NotificationContextType {
  notifications: IWorkflowNotification[];
  connectedCount: number;
  isConnected: boolean;
  resetCount: () => void;
  subscribeToAudience: (audienceId: string | number, skipTracking?: boolean) => void;
  unsubscribeFromAudience: () => void;
  getNotificationsForAudience: (audienceId: number) => IWorkflowNotification[];
  hasActiveNotifications: (audienceId: number) => boolean;
  hasNotifications: (audienceId: number) => boolean;
  clearNotificationsForAudience: (audienceId: number) => void;
  /**
   * Clear all notifications across all audiences.
   * Removes from both React state and localStorage.
   */
  clearAllNotifications: () => void;
  /**
   * Epoch ms the user last opened the notifications drawer (0 = never).
   * Used with countUnseenActiveNotifications to drive the bell badge.
   */
  notificationsSeenAt: number;
  /**
   * Mark current notifications as seen (call when the drawer opens) so the
   * badge clears and does not stick on stale active notifications.
   */
  markNotificationsSeen: () => void;
  /**
   * Reconcile localStorage notifications with API-derived status.
   * When API says workflow is completed but we have stale "started" notifications,
   * mark them as "completed" to sync UI with backend truth.
   * This handles cases where WebSocket "completed" notifications were missed.
   */
  reconcileWithApiStatus: (audienceId: number, apiDerivedStatus: string) => void;
  // Dialer session management
  dialerSessions: Map<number, DialerSessionData>;
  getDialerSession: (audienceId: number) => DialerSessionData | null;
  setDialerSession: (audienceId: number, session: DialerSessionData) => void;
  clearDialerSession: (audienceId: number) => void;
  // Persona content generation subscriptions
  subscribeToPersona: (personaId: string) => void;
  unsubscribeFromPersona: (personaId: string) => void;
  onPersonaUpdate: (callback: PersonaUpdateCallback) => () => void;
  // Persona finder (on-demand enrichment) subscriptions
  subscribeToPersonaFinder: (requestId: string) => void;
  unsubscribeFromPersonaFinder: (requestId: string) => void;
  onPersonaFinderResult: (callback: PersonaFinderCallback) => () => void;
}

export const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const { user, accessToken } = useAuth();
  const workspaceContext = useWorkspace();
  const activeWorkspace = workspaceContext?.activeWorkspace;

  const [isConnected, setIsConnected] = useState(socketIo.connected);
  const [notifications, setNotifications] = useState<IWorkflowNotification[]>([]);
  // Epoch ms the user last opened the notifications drawer (workspace-scoped,
  // persisted). Drives the unseen-active badge count so opening clears it and
  // stale stuck-active notifications don't keep the badge lit. 0 = never seen.
  const [notificationsSeenAt, setNotificationsSeenAt] = useState<number>(0);
  const [connectedCount, setConnectedCount] = useState(0);
  const [dialerSessions, setDialerSessions] = useState<Map<number, DialerSessionData>>(new Map());
  const notificationKeysRef = useRef<Set<string>>(new Set());
  const currentAudienceRef = useRef<string | number | null>(null);
  const isInitializedRef = useRef(false);
  const isCleaningUpRef = useRef(false);
  const subscribedAudiencesRef = useRef<Set<number>>(new Set());
  const pendingAudienceSubscriptionBatchRef = useRef<number[]>([]);
  const subscribedPersonasRef = useRef<Set<string>>(new Set());
  const personaUpdateCallbacksRef = useRef<Set<PersonaUpdateCallback>>(new Set());
  // Recently-dispatched persona_update completion keys → timestamp. The backend emits the
  // same persona_update to both persona:{id} and workspace_{vendorid}_updates for
  // redundancy, so a client in both rooms receives it twice; dedupe on the completion key
  // so callbacks (toast, version bump) fire once.
  const processedPersonaUpdatesRef = useRef<Map<string, number>>(new Map());
  const subscribedPersonaFindersRef = useRef<Set<string>>(new Set());
  const personaFinderCallbacksRef = useRef<Set<PersonaFinderCallback>>(new Set());
  const previousWorkspaceIdRef = useRef<number | undefined>(undefined);

  // Authenticate the transport before any protected room subscription is sent.
  // Reconnect on token rotation so every room authorization uses current,
  // server-verified Supabase identity rather than caller-supplied payload data.
  useEffect(() => {
    if (!accessToken) {
      socketIo.auth = {};
      if (socketIo.connected) socketIo.disconnect();
      return;
    }

    const previousToken = (socketIo.auth as { token?: string })?.token;
    socketIo.auth = { token: accessToken };
    if (socketIo.connected && previousToken !== accessToken) {
      socketIo.disconnect();
    }
    if (!socketIo.connected) socketIo.connect();
  }, [accessToken]);

  // Use workspace-aware socket management
  useWorkspaceSocket(socketIo);

  // Debounced localStorage save for non-critical updates (cleanup, reconciliation)
  // 200ms delay - shorter for better responsiveness
  const debouncedSaveToStorage = useDebouncedCallback(
    (notificationsToSave: IWorkflowNotification[], workspaceId: number) => {
      saveNotificationsToStorage(notificationsToSave, workspaceId);
    },
    200 // 200ms debounce - faster than before
  );

  // Ref to track pending saves for beforeunload handler
  const pendingSaveRef = useRef<{ notifications: IWorkflowNotification[]; workspaceId: number } | null>(null);

  // Immediate save function for critical workflow notifications (no debounce)
  const immediateSaveToStorage = useCallback((notificationsToSave: IWorkflowNotification[], workspaceId: number) => {
    saveNotificationsToStorage(notificationsToSave, workspaceId);
    pendingSaveRef.current = null; // Clear pending since we just saved
  }, []);

  // beforeunload handler to flush pending saves before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Flush any pending debounced saves
      if (pendingSaveRef.current) {
        saveNotificationsToStorage(pendingSaveRef.current.notifications, pendingSaveRef.current.workspaceId);
        pendingSaveRef.current = null;
      }
      // Also save current state immediately
      if (activeWorkspace?.vendorid && notifications.length > 0) {
        saveNotificationsToStorage(notifications, activeWorkspace.vendorid);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [activeWorkspace?.vendorid, notifications]);

  // Load notifications from localStorage on mount and workspace change
  useEffect(() => {
    if (!activeWorkspace?.vendorid) return;

    // Reset initialization state when workspace changes
    if (previousWorkspaceIdRef.current !== undefined && previousWorkspaceIdRef.current !== activeWorkspace.vendorid) {
      // Workspace changed - reset state
      appLogger.info('[NotificationProvider] Workspace changed, resetting state', {
        previousWorkspaceId: previousWorkspaceIdRef.current,
        newWorkspaceId: activeWorkspace.vendorid,
      });
      
      // Clear subscriptions for old workspace
      subscribedAudiencesRef.current.clear();
      pendingAudienceSubscriptionBatchRef.current = [];
      notificationKeysRef.current.clear();
      isInitializedRef.current = false;
      setNotifications([]);
      setConnectedCount(0);
    }
    previousWorkspaceIdRef.current = activeWorkspace.vendorid;

    // Skip if already initialized for this workspace
    if (isInitializedRef.current) return;

    const stored = loadNotificationsFromStorage(activeWorkspace.vendorid);
    const cleaned = cleanupNotifications(stored);
    
    // Initialize notification keys from stored notifications
    cleaned.forEach((notification) => {
      notificationKeysRef.current.add(getNotificationKey(notification));
    });

    setNotifications(cleaned);
    setNotificationsSeenAt(loadSeenAtFromStorage(activeWorkspace.vendorid));
    isInitializedRef.current = true;

    appLogger.debug('[NotificationProvider] Initialized from localStorage', {
      storedCount: stored.length,
      cleanedCount: cleaned.length,
      workspaceId: activeWorkspace.vendorid,
    });
  }, [activeWorkspace?.vendorid]);

  // Save notifications to localStorage whenever they change
  // Uses immediate save for new workflow notifications to prevent data loss
  useEffect(() => {
    if (!activeWorkspace?.vendorid || !isInitializedRef.current || isCleaningUpRef.current) return;

    const cleaned = cleanupNotifications(notifications);

    // Only save if cleaned notifications differ from current (avoid infinite loop)
    if (cleaned.length !== notifications.length) {
      // Cleanup removed some notifications, update state first
      isCleaningUpRef.current = true;
      setNotifications(cleaned);
      // Save the cleaned version immediately (cleanup is important)
      immediateSaveToStorage(cleaned, activeWorkspace.vendorid);
      // Reset cleanup flag after state update
      setTimeout(() => {
        isCleaningUpRef.current = false;
      }, 0);
    } else {
      // Save immediately - don't risk losing workflow notifications
      // Track as pending in case page unloads during save
      pendingSaveRef.current = { notifications, workspaceId: activeWorkspace.vendorid };
      immediateSaveToStorage(notifications, activeWorkspace.vendorid);
    }
  }, [notifications, activeWorkspace?.vendorid, immediateSaveToStorage]);

  // Generate secure, workspace-scoped session ID
  const userSession = useMemo(() => {
    if (!activeWorkspace || !user) return null;
    return `workspace_${activeWorkspace.vendorid}_user_${user.id}_${Date.now()}`;
  }, [activeWorkspace?.vendorid, user?.id]);

  // Refs to hold current values for socket handler (avoids stale closures)
  const dialerSessionsRef = useRef(dialerSessions);

  /**
   * Dialer Session Management
   * Centralized state for Human Dialer modal sessions
   */
  const getDialerSession = useCallback((audienceId: number): DialerSessionData | null => {
    return dialerSessionsRef.current.get(audienceId) || null;
  }, []);

  const setDialerSession = useCallback((audienceId: number, session: DialerSessionData) => {
    appLogger.info('[NotificationProvider] Setting dialer session', {
      audienceId,
      sessionId: session.session_id,
      conferenceName: session.conference_name,
    });
    setDialerSessions(prev => {
      const next = new Map(prev);
      next.set(audienceId, session);
      return next;
    });
  }, []);

  const clearDialerSession = useCallback((audienceId: number) => {
    appLogger.info('[NotificationProvider] Clearing dialer session', { audienceId });
    setDialerSessions(prev => {
      const next = new Map(prev);
      next.delete(audienceId);
      return next;
    });
  }, []);
  const userSessionRef = useRef(userSession);
  const activeWorkspaceRef = useRef(activeWorkspace);
  const userRef = useRef(user);

  // Keep refs in sync with state
  useEffect(() => {
    dialerSessionsRef.current = dialerSessions;
  }, [dialerSessions]);

  useEffect(() => {
    userSessionRef.current = userSession;
    activeWorkspaceRef.current = activeWorkspace;
    userRef.current = user;
  }, [userSession, activeWorkspace, user]);

  const subscribeToAudiencesBatch = useCallback((
    audienceIds: Iterable<string | number>,
    includeTracked = false,
  ): number[] => {
    const currentUserSession = userSessionRef.current;
    const currentWorkspace = activeWorkspaceRef.current;
    const currentUser = userRef.current;

    if (!currentUserSession || !currentWorkspace || !currentUser) {
      appLogger.warn('socket_batch_subscription_missing_context', {
        component: 'NotificationProvider',
        hasUserSession: !!currentUserSession,
        hasActiveWorkspace: !!currentWorkspace,
        hasUser: !!currentUser,
      });
      return [];
    }

    const emittedAudienceIds = emitAudienceSubscriptionBatch({
      socket: socketIo,
      audienceIds,
      trackedAudienceIds: subscribedAudiencesRef.current,
      userSession: currentUserSession,
      workspaceId: currentWorkspace.vendorid,
      userId: currentUser.id,
      includeTracked,
    });

    if (emittedAudienceIds.length > 0) {
      pendingAudienceSubscriptionBatchRef.current = emittedAudienceIds;
    }
    return emittedAudienceIds;
  }, []);

  // CRITICAL: Socket.IO run_update handler - must be attached IMMEDIATELY on mount
  // This effect has NO dependencies to ensure handler is always attached from the start
  // This prevents race conditions where events arrive before auth context is ready
  useEffect(() => {
    appLogger.debug('socket_run_update_handler_setup', {
      component: 'NotificationProvider',
      socketConnected: socketIo.connected,
      socketId: socketIo.id,
    });

    const handleRunUpdate = (data: IWorkflowNotification) => {
      appLogger.info('[Socket.IO] Global run_update received:', {
        data,
        type: data.type,
        state: data.state,
        audienceId: data.audienceId,
        timestamp: data.timestamp,
      });

      // Handle dialer session events (use refs for current state)
      if (data.type === 'dialer_session_created') {
        // Backend sends session_id and conference_name at top level, not in raw
        const eventData = data as any; // Type assertion to access backend event fields

        if (data.state === 'web_calling') {
          // Check for duplicate session (prevent worker from creating duplicates)
          const currentSession = dialerSessionsRef.current.get(data.audienceId);
          if (currentSession && currentSession.session_id === eventData.session_id) {
            appLogger.warn('[Socket.IO] Duplicate dialer session creation ignored', {
              sessionId: eventData.session_id,
              audienceId: data.audienceId,
              reason: 'Same session already active',
            });
            return; // Don't process duplicate
          }

          // Validate required fields
          if (eventData.session_id && eventData.conference_name && data.audienceId) {
            // Replace existing session if new session_id (new campaign run)
            if (currentSession && currentSession.session_id !== eventData.session_id) {
              appLogger.info('[Socket.IO] Replacing existing session with new session', {
                oldSessionId: currentSession.session_id,
                newSessionId: eventData.session_id,
                audienceId: data.audienceId,
                reason: 'New campaign started',
              });
            }

            setDialerSession(data.audienceId, {
              session_id: eventData.session_id,
              conference_name: eventData.conference_name,
              audienceId: data.audienceId,
              timestamp: data.timestamp || new Date().toISOString(),
            });
          } else {
            appLogger.error('[Socket.IO] Invalid dialer session data', {
              hasSessionId: !!eventData.session_id,
              hasConferenceName: !!eventData.conference_name,
              hasAudienceId: !!data.audienceId,
              data,
            });
          }
        } else if (data.state === 'session_terminated' || data.state === 'session_completed') {
          // Clear dialer session when terminated
          if (data.audienceId) {
            clearDialerSession(data.audienceId);
          }
        }
      }

      // Handle dialer session updates (terminal states - auto-close modal)
      if (data.type === 'dialer_session_updated') {
        const eventData = data as any;
        const terminalStates = ['session_cancelled', 'completed', 'cancelled', 'failed', 'all_prospects_completed'];

        if (terminalStates.includes(data.state)) {
          appLogger.info('[Socket.IO] Terminal session state detected - clearing dialer session', {
            state: data.state,
            sessionId: eventData.session_id,
            audienceId: data.audienceId,
            reason: eventData.reason || 'Workflow completed or cancelled',
          });

          // Only clear if this event is for the current session
          const currentSession = dialerSessionsRef.current.get(data.audienceId);
          if (currentSession && currentSession.session_id === eventData.session_id) {
            clearDialerSession(data.audienceId);
          }
        }
      }

      // Socket events also drive live product state. Only events with actual
      // user-facing content belong in Workflow Progress or its badge.
      if (filterUserFacingNotifications([data]).length === 0) return;

      const incomingKey = getNotificationKey(data);

      const isDupe = notificationKeysRef.current.has(incomingKey);

      if (!isDupe) {
        notificationKeysRef.current.add(incomingKey);
        setConnectedCount((count) => {
          const newCount = count + 1;
          appLogger.info('[Socket.IO] NEW GLOBAL NOTIFICATION! Count now:', { data: newCount });
          return newCount;
        });
        // Merge with existing notifications (from localStorage and socket)
        setNotifications((prev) => {
          // If this is a new execution starting, clean up old completed notifications for this audience
          const cleaned = cleanupOldCompletedOnNewExecution(prev, data);
          const merged = mergeNotifications(cleaned, [data]);
          return merged;
        });
      } else {
        appLogger.info('[Socket.IO] Duplicate notification ignored');
      }
    };

    // Attach handler immediately - no waiting for auth context
    socketIo.on('run_update', handleRunUpdate);

    // Handle persona_update events (content generation completion from personaUpdatesConsumer)
    const handlePersonaUpdate = (data: PersonaUpdateEvent) => {
      appLogger.info('[Socket.IO] persona_update received:', {
        event: data.event,
        personaId: data.personaId,
        audienceIds: data.audienceIds,
        timestamp: data.timestamp,
      });

      // Dedupe the redundant dual-room delivery (persona:{id} + workspace_{vendorid}_updates)
      // so callbacks run once. Key on the completion identity; evict entries older than 60s.
      const dedupeKey = `${data.personaId}:${data.event}:${data.completedAt}`;
      const nowMs = Date.now();
      processedPersonaUpdatesRef.current.forEach((ts, key) => {
        if (nowMs - ts > 60_000) processedPersonaUpdatesRef.current.delete(key);
      });
      if (processedPersonaUpdatesRef.current.has(dedupeKey)) {
        return;
      }
      processedPersonaUpdatesRef.current.set(dedupeKey, nowMs);

      // Dispatch to all registered callbacks
      personaUpdateCallbacksRef.current.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          appLogger.trackError(error as Error, {
            component: 'NotificationProvider',
            message: 'Error in persona_update callback',
            personaId: data.personaId,
          });
        }
      });
    };

    socketIo.on('persona_update', handlePersonaUpdate);

    // Handle persona_finder_result events (on-demand enrichment results)
    const handlePersonaFinderResult = (data: PersonaFinderResultEvent) => {
      appLogger.info('[Socket.IO] persona_finder_result received:', {
        event: data.event,
        requestId: data.requestId,
      });

      personaFinderCallbacksRef.current.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          appLogger.trackError(error as Error, {
            component: 'NotificationProvider',
            message: 'Error in persona_finder_result callback',
            requestId: data.requestId,
          });
        }
      });
    };

    socketIo.on('persona_finder_result', handlePersonaFinderResult);

    return () => {
      socketIo.off('run_update', handleRunUpdate);
      socketIo.off('persona_update', handlePersonaUpdate);
      socketIo.off('persona_finder_result', handlePersonaFinderResult);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - attach once on mount, never re-attach

  // Socket.IO connection and other event handlers (these CAN depend on auth context)
  useEffect(() => {
    appLogger.debug('socket_connection_handlers_setup', {
      component: 'NotificationProvider',
      userSession,
      workspaceId: activeWorkspace?.vendorid,
      userId: user?.id,
      currentAudience: currentAudienceRef.current,
      socketConnected: socketIo.connected,
      socketId: socketIo.id,
    });

    const handleConnect = () => {
      appLogger.info('socket_connected', {
        component: 'NotificationProvider',
        socketId: socketIo.id,
        socketUrl: process.env.NEXT_PUBLIC_SOCKET_URL,
        socketPath: process.env.NEXT_PUBLIC_SOCKET_IO_HUB_PATH,
        workspaceId: activeWorkspace?.vendorid,
        userId: user?.id,
      });
      setIsConnected(true);

      // Rejoin the per-user workspace room on every (re)connect. useWorkspaceSocket
      // only emits join_room when the active workspace changes — a plain transport-
      // close + reconnect would otherwise leave the socket out of
      // workspace_{vendorid}_user_{userId}, causing on-demand persona_finder_result
      // events to land in an empty room. Socket.IO treats a second join as a no-op,
      // so this is safe when the room membership already exists.
      if (activeWorkspace?.vendorid && user?.id) {
        socketIo.emit('join_room', {
          room: `workspace_${activeWorkspace.vendorid}_user_${user.id}`,
        });
      }

      // Join the per-workspace run-update room on every (re)connect. The backend emits
      // every run_update to workspace_{vendorid}, so this single group join replaces the
      // legacy "subscribe to every audience" fan-out (which joined one Azure Web PubSub
      // group per audience — thousands on large workspaces). Per-audience subscriptions
      // below remain only for the small working set of audiences the user actually opens.
      if (activeWorkspace?.vendorid) {
        socketIo.emit('join_room', {
          room: `workspace_${activeWorkspace.vendorid}`,
        });
      }

      // Also (re)join the per-workspace *updates* room. The backend broadcasts persona
      // content_generated / persona_update events to workspace_{vendorid}_updates as a
      // redundancy path (see personaUpdatesConsumer). If a plain transport reconnect drops
      // the persona:{id} room membership, the primary event is lost — without also being in
      // the updates room the completion never arrives and the task sits on "Finalizing
      // content" until the watchdog times out. persona_update is filtered by personaId on
      // the client, so receiving it via the workspace room is safe.
      if (activeWorkspace?.vendorid) {
        socketIo.emit('join_room', {
          room: `workspace_${activeWorkspace.vendorid}_updates`,
        });
      }

      // Re-subscribe to all previously subscribed audiences on reconnect
      if (userSession && activeWorkspace && user && subscribedAudiencesRef.current.size > 0) {
        appLogger.info('socket_resubscribe_after_reconnect', {
          component: 'NotificationProvider',
          subscribedCount: subscribedAudiencesRef.current.size,
          workspaceId: activeWorkspace.vendorid,
          userId: user.id,
        });

        // Room membership is lost on disconnect, so include already tracked IDs.
        subscribeToAudiencesBatch(subscribedAudiencesRef.current, true);
      }

      // Re-subscribe to tracked personas + in-flight persona finders on reconnect,
      // INDEPENDENT of whether any audiences are tracked. A persona detail panel can be
      // open with zero subscribed audiences; previously these loops were nested inside the
      // audiences.size > 0 guard, so a reconnect left the socket out of the persona:{id}
      // room and dropped the content_generated completion event (root cause of the stuck
      // "Finalizing content").
      if (userSession && activeWorkspace && user) {
        subscribedPersonasRef.current.forEach((personaId) => {
          socketIo.emit('subscribe_persona', {
            personaId,
            userSession,
          });
        });

        subscribedPersonaFindersRef.current.forEach((requestId) => {
          socketIo.emit('subscribe_persona_finder', { requestId });
        });
      }
    };

    const handleDisconnect = (reason: string) => {
      appLogger.warn('socket_disconnected', {
        component: 'NotificationProvider',
        reason,
        workspaceId: activeWorkspace?.vendorid,
        userId: user?.id,
      });
      setIsConnected(false);

      // Track disconnect in Datadog RUM (only for unexpected disconnects)
      if (reason !== 'io client disconnect') {
        appLogger.trackAction('socket_disconnected', {
          component: 'NotificationProvider',
          reason,
          workspaceId: activeWorkspace?.vendorid,
          userId: user?.id,
        });
      }
    };

    const handleConnectError = (error: Error) => {
      // Transient connection errors during reconnection attempts are expected
      // Only track as error if it's a persistent issue (logged at warning level by default)
      const isTransientError = error.message?.includes('timeout') ||
                               error.message?.includes('ECONNREFUSED') ||
                               error.message?.includes('xhr poll error') ||
                               error.message?.includes('websocket error');

      if (isTransientError) {
        // Log as warning for transient errors - Socket.IO will auto-reconnect
        appLogger.warn('socket_connection_retry', {
          component: 'NotificationProvider',
          message: 'Socket.IO connection error (will auto-reconnect)',
          errorMessage: error.message,
          workspaceId: activeWorkspace?.vendorid,
          userId: user?.id,
        });
      } else {
        // Log as error for unexpected connection issues
        appLogger.trackError(error, {
          component: 'NotificationProvider',
          message: 'Socket.IO connection error',
          source: 'websocket',
          socket_url: process.env.NEXT_PUBLIC_SOCKET_URL,
          socket_path: process.env.NEXT_PUBLIC_SOCKET_IO_HUB_PATH,
          workspaceId: activeWorkspace?.vendorid,
          userId: user?.id,
        });
      }
    };

    const handleError = (error: any) => {
      appLogger.error('[Socket.IO] Server error received:', {
        component: 'NotificationProvider',
        error: error.error || error.message || error,
        workspaceId: activeWorkspace?.vendorid,
        userId: user?.id,
      });
    };

    const handleRoomError = (payload: { room?: string; error?: string }) => {
      const room = payload?.room || '';
      const protectsWorkspace = room.startsWith('bot_inbox_') || room.startsWith('workspace_');
      if (protectsWorkspace) setIsConnected(false);

      if (room === 'audience:*') {
        const deniedAudienceIds = pendingAudienceSubscriptionBatchRef.current;
        pendingAudienceSubscriptionBatchRef.current = [];

        const currentUserSession = userSessionRef.current;
        const currentWorkspace = activeWorkspaceRef.current;
        const currentUser = userRef.current;
        if (deniedAudienceIds.length > 0 && currentUserSession && currentWorkspace && currentUser) {
          retryDeniedAudienceSubscriptionBatch({
            socket: socketIo,
            audienceIds: deniedAudienceIds,
            trackedAudienceIds: subscribedAudiencesRef.current,
            userSession: currentUserSession,
            workspaceId: currentWorkspace.vendorid,
            userId: currentUser.id,
          });
        }
      } else {
        removeDeniedAudienceSubscription(subscribedAudiencesRef.current, room);
      }

      appLogger.error('socket_room_authorization_failed', {
        component: 'NotificationProvider',
        room,
        error: payload?.error || 'Unauthorized room',
        workspaceId: activeWorkspace?.vendorid,
        userId: user?.id,
      });
    };

    const handleSubscriptionBatchConfirmed = () => {
      pendingAudienceSubscriptionBatchRef.current = [];
    };

    // Add event listeners (run_update is handled separately in its own effect)
    appLogger.debug('socket_connection_listeners_attached', {
      component: 'NotificationProvider',
      socketId: socketIo.id,
    });
    socketIo.on('connect', handleConnect);
    socketIo.on('disconnect', handleDisconnect);
    socketIo.on('connect_error', handleConnectError);
    socketIo.on('error', handleError);
    socketIo.on('room_error', handleRoomError);
    socketIo.on('subscription_batch_confirmed', handleSubscriptionBatchConfirmed);

    // If already connected, run handleConnect directly. The 'connect' event already fired
    // before this effect attached its listener (typical on initial mount — Socket.IO connects
    // in <100ms while React effects schedule on the next tick), so handleConnect would
    // otherwise never run and the socket would never emit join_room for the per-user workspace
    // room. The result: on-demand persona_finder_result events land in an empty room and the
    // FE keeps spinning forever even though the worker already returned a valid result.
    // handleConnect is idempotent (Socket.IO treats a second join as a no-op).
    if (socketIo.connected) {
      appLogger.debug('socket_already_connected', {
        component: 'NotificationProvider',
        socketId: socketIo.id,
      });
      handleConnect();
    } else {
      appLogger.debug('socket_waiting_for_connection', {
        component: 'NotificationProvider',
      });
    }

    return () => {
      appLogger.debug('socket_connection_listeners_cleanup', {
        component: 'NotificationProvider',
      });
      socketIo.off('connect', handleConnect);
      socketIo.off('disconnect', handleDisconnect);
      socketIo.off('connect_error', handleConnectError);
      socketIo.off('error', handleError);
      socketIo.off('room_error', handleRoomError);
      socketIo.off('subscription_batch_confirmed', handleSubscriptionBatchConfirmed);
    };
  }, [userSession, activeWorkspace?.vendorid, user?.id, subscribeToAudiencesBatch]);

  const subscribeToAudience = useCallback((audienceId: string | number, skipTracking = false) => {
    if (!userSession || !activeWorkspace || !user) {
      appLogger.warn('socket_subscription_missing_context', {
        component: 'NotificationProvider',
        audienceId,
        hasUserSession: !!userSession,
        hasActiveWorkspace: !!activeWorkspace,
        hasUser: !!user,
      });
      return;
    }

    const audienceIdNum = typeof audienceId === 'string' ? parseInt(audienceId, 10) : audienceId;
    
    // Skip if already subscribed (unless skipTracking is true for manual tracking)
    if (!skipTracking && subscribedAudiencesRef.current.has(audienceIdNum)) {
      appLogger.debug('socket_subscribe_skipped_already_subscribed', {
        component: 'NotificationProvider',
        audienceId: audienceIdNum,
      });
      return;
    }

    const channel = `audience:${audienceId}`;

    appLogger.info('socket_subscribe', {
      component: 'NotificationProvider',
      audienceId,
      channel,
      userSession,
      workspaceId: activeWorkspace.vendorid,
      userId: user.id,
      socketConnected: socketIo.connected,
      socketId: socketIo.id,
    });

    // Track subscription
    if (!skipTracking) {
      subscribedAudiencesRef.current.add(audienceIdNum);
    }

    // Store the audience ID for re-subscription on connect/reconnect
    currentAudienceRef.current = audienceId;

    const emitSubscription = () => {
      socketIo.emit('subscribe_audience', {
        audienceId,
        userSession,
        workspaceId: activeWorkspace.vendorid,
        userId: user.id,
      });
      appLogger.debug('socket_subscribe_emitted', {
        component: 'NotificationProvider',
        audienceId,
        channel,
        userSession,
        workspaceId: activeWorkspace.vendorid,
        userId: user.id,
      });
    };

    if (socketIo.connected) {
      emitSubscription();
    } else {
      appLogger.warn('socket_subscribe_pending_connection', {
        component: 'NotificationProvider',
        audienceId,
        channel,
      });
      // Wait for socket to connect, then subscribe
      // Use 'once' to avoid duplicate subscriptions
      const onConnect = () => {
        appLogger.info('socket_subscribe_on_connect', {
          component: 'NotificationProvider',
          audienceId,
          channel,
          socketId: socketIo.id,
        });
        emitSubscription();
      };
      socketIo.once('connect', onConnect);
    }
  }, [userSession, activeWorkspace, user]);

  const unsubscribeFromAudience = useCallback(() => {
    appLogger.info('socket_unsubscribe', {
      component: 'NotificationProvider',
      audienceId: currentAudienceRef.current ?? undefined,
    });
    currentAudienceRef.current = null;
  }, []);

  const subscribeToPersona = useCallback((personaId: string) => {
    if (!userSession || !activeWorkspace || !user) {
      appLogger.warn('socket_persona_subscription_missing_context', {
        component: 'NotificationProvider',
        personaId,
        hasUserSession: !!userSession,
        hasActiveWorkspace: !!activeWorkspace,
        hasUser: !!user,
      });
      return;
    }

    if (subscribedPersonasRef.current.has(personaId)) {
      return;
    }

    appLogger.info('socket_subscribe_persona', {
      component: 'NotificationProvider',
      personaId,
      workspaceId: activeWorkspace.vendorid,
    });

    subscribedPersonasRef.current.add(personaId);

    const emitSubscription = () => {
      socketIo.emit('subscribe_persona', {
        personaId,
        userSession,
      });
    };

    if (socketIo.connected) {
      emitSubscription();
    } else {
      socketIo.once('connect', emitSubscription);
    }
  }, [userSession, activeWorkspace, user]);

  const unsubscribeFromPersona = useCallback((personaId: string) => {
    if (!subscribedPersonasRef.current.has(personaId)) {
      return;
    }

    appLogger.info('socket_unsubscribe_persona', {
      component: 'NotificationProvider',
      personaId,
    });

    subscribedPersonasRef.current.delete(personaId);

    if (socketIo.connected) {
      socketIo.emit('unsubscribe_persona', { personaId });
    }
  }, []);

  const onPersonaUpdate = useCallback((callback: PersonaUpdateCallback): (() => void) => {
    personaUpdateCallbacksRef.current.add(callback);
    return () => {
      personaUpdateCallbacksRef.current.delete(callback);
    };
  }, []);

  const subscribeToPersonaFinder = useCallback((requestId: string) => {
    if (subscribedPersonaFindersRef.current.has(requestId)) {
      return;
    }

    appLogger.info('socket_subscribe_persona_finder', {
      component: 'NotificationProvider',
      requestId,
    });

    subscribedPersonaFindersRef.current.add(requestId);

    const emitSubscription = () => {
      socketIo.emit('subscribe_persona_finder', { requestId });
    };

    if (socketIo.connected) {
      emitSubscription();
    } else {
      socketIo.once('connect', emitSubscription);
    }
  }, []);

  const unsubscribeFromPersonaFinder = useCallback((requestId: string) => {
    if (!subscribedPersonaFindersRef.current.has(requestId)) {
      return;
    }

    subscribedPersonaFindersRef.current.delete(requestId);

    if (socketIo.connected) {
      socketIo.emit('unsubscribe_persona_finder', { requestId });
    }
  }, []);

  const onPersonaFinderResult = useCallback((callback: PersonaFinderCallback): (() => void) => {
    personaFinderCallbacksRef.current.add(callback);
    return () => {
      personaFinderCallbacksRef.current.delete(callback);
    };
  }, []);

  // Subscribe to audiences with existing notifications using one authorization query.
  useEffect(() => {
    if (!activeWorkspace?.vendorid || !isInitializedRef.current) return;

    const subscribeToStoredNotificationAudiences = () => {
      const stored = loadNotificationsFromStorage(activeWorkspace.vendorid);
      const cleaned = cleanupNotifications(stored);
      const audienceIdsWithNotifications = new Set(cleaned.map((notification) => notification.audienceId));
      const subscribedAudienceIds = subscribeToAudiencesBatch(audienceIdsWithNotifications);

      appLogger.debug('[NotificationProvider] Subscribed to audiences with notifications', {
        count: subscribedAudienceIds.length,
        audienceIds: subscribedAudienceIds,
      });
    };

    // Wait for socket connection
    if (!socketIo.connected) {
      socketIo.once('connect', subscribeToStoredNotificationAudiences);
      return () => {
        socketIo.off('connect', subscribeToStoredNotificationAudiences);
      };
    }

    subscribeToStoredNotificationAudiences();
  }, [activeWorkspace?.vendorid, subscribeToAudiencesBatch]);

  // NOTE: The legacy "subscribe to every audience on mount" effect was removed here.
  // It joined one Azure Web PubSub group per audience (thousands on large workspaces) on
  // every load, workspace switch, and reconnect — almost all idle and redundant. Run
  // updates now arrive via the per-workspace room (workspace_{vendorid}, joined once in
  // handleConnect). Per-audience subscriptions remain only for the small working set the
  // user actually opens (WorkflowExecutionSheet, PersonDetailPanel, etc.) and for
  // audiences with stored notifications (effect above), both of which flush buffered
  // events on join.

  const resetCount = useCallback(() => {
    setConnectedCount(0);
  }, []);

  /**
   * Get all notifications for a specific audience
   */
  const getNotificationsForAudience = useCallback((audienceId: number): IWorkflowNotification[] => {
    const audienceNotifications = filterUserFacingNotifications(
      notifications.filter((n) => n.audienceId === audienceId),
    );
    // Sort by timestamp ascending (oldest first) - create new array to avoid mutation
    return [...audienceNotifications].sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeA - timeB; // Ascending order (oldest first)
    });
  }, [notifications]);

  /**
   * Check if an audience has active notifications (not completed/error states)
   * Active means: processing, running, or any state that indicates workflow is still running
   */
  const hasActiveNotifications = useCallback((audienceId: number): boolean => {
    const audienceNotifications = filterUserFacingNotifications(
      notifications.filter((n) => n.audienceId === audienceId),
    );
    if (audienceNotifications.length === 0) {
      appLogger.debug('[hasActiveNotifications] No notifications for audience', { audienceId });
      return false;
    }

    appLogger.debug('[hasActiveNotifications] Checking notifications', {
      audienceId,
      count: audienceNotifications.length,
      notifications: audienceNotifications.map(n => ({ state: n.state, timestamp: n.timestamp })),
    });

    // Check if the most recent notification is not final
    // This is the primary check - if the latest notification is not final, workflow is still active
    const mostRecent = audienceNotifications[audienceNotifications.length - 1];
    if (mostRecent) {
      const state = mostRecent.state?.toLowerCase() || '';
      const isFinal = state.includes('completed') || 
                     state.includes('success') || 
                     state.includes('error') || 
                     state.includes('failed') ||
                     state.includes('cancelled');
      
      const isActive = !isFinal;
      appLogger.debug('[hasActiveNotifications] Most recent notification check', {
        audienceId,
        state,
        isFinal,
        isActive,
      });
      
      if (isActive) return true;
    }

    // Fallback: Check if there are any notifications that indicate active processing
    const hasActiveState = audienceNotifications.some((n) => {
      const state = n.state?.toLowerCase() || '';
      const isFinal = state.includes('completed') || 
                     state.includes('success') || 
                     state.includes('error') || 
                     state.includes('failed') ||
                     state.includes('cancelled');
      return !isFinal;
    });

    appLogger.debug('[hasActiveNotifications] Final result', {
      audienceId,
      hasActiveState,
    });

    return hasActiveState;
  }, [notifications]);

  /**
   * Check if an audience has any notifications (regardless of state)
   * Used to show notification button so users can view execution history
   */
  const hasNotifications = useCallback((audienceId: number): boolean => {
    const audienceNotifications = filterUserFacingNotifications(
      notifications.filter((n) => n.audienceId === audienceId),
    );
    const hasAny = audienceNotifications.length > 0;
    
    appLogger.debug('[hasNotifications] Checking if audience has any notifications', {
      audienceId,
      count: audienceNotifications.length,
      hasAny,
    });
    
    return hasAny;
  }, [notifications]);

  /**
   * Clear all notifications for a specific audience
   * Used when transitioning from test to production workflow to clear test notifications
   */
  const clearNotificationsForAudience = useCallback((audienceId: number) => {
    setNotifications((prev) => {
      const toRemove = prev.filter((n) => n.audienceId === audienceId);
      const filtered = prev.filter((n) => n.audienceId !== audienceId);
      // Remove dedup keys for cleared notifications so new events for the same
      // audience aren't silently dropped by the duplicate-detection guard.
      toRemove.forEach((n) => notificationKeysRef.current.delete(getNotificationKey(n)));
      appLogger.debug('[clearNotificationsForAudience] Cleared notifications for audience', {
        audienceId,
        removedCount: toRemove.length,
        remainingCount: filtered.length,
      });
      return filtered;
    });
    // Also clear from localStorage
    if (activeWorkspace?.vendorid) {
      const stored = loadNotificationsFromStorage(activeWorkspace.vendorid);
      const filtered = stored.filter((n) => n.audienceId !== audienceId);
      saveNotificationsToStorage(filtered, activeWorkspace.vendorid);
    }
  }, [activeWorkspace?.vendorid]);

  /**
   * Clear all notifications across all audiences
   * Used when user explicitly dismisses all notifications from the drawer
   */
  /**
   * Mark all current notifications as seen (called when the drawer opens).
   * Records "now" so the badge stops counting everything up to this moment;
   * genuinely-new activity afterwards still re-badges. Persisted per workspace
   * so the badge stays cleared across reloads.
   */
  const markNotificationsSeen = useCallback(() => {
    const now = Date.now();
    setNotificationsSeenAt(now);
    const storageKey = getSeenAtStorageKey(activeWorkspace?.vendorid);
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, String(now));
      } catch {
        // Non-fatal: badge will still clear in-memory for this session.
      }
    }
  }, [activeWorkspace?.vendorid]);

  const clearAllNotifications = useCallback(() => {
    const prevCount = notifications.length;
    setNotifications([]);
    appLogger.debug('[clearAllNotifications] Cleared all notifications', {
      removedCount: prevCount,
    });
    // Also clear from localStorage
    if (activeWorkspace?.vendorid) {
      saveNotificationsToStorage([], activeWorkspace.vendorid);
    }
  }, [activeWorkspace?.vendorid, notifications.length]);

  /**
   * Reconcile localStorage notifications with API-derived status.
   * This is the backfill mechanism: when API says workflow is completed/replied
   * but we have stale "started"/"processing" notifications, mark them as "completed"
   * to sync the UI with backend truth.
   *
   * This handles cases where WebSocket "completed" notifications were missed due to:
   * - Network disconnection during completion
   * - Service Bus message throttling
   * - Worker crashes before sending completion
   */
  const reconcileWithApiStatus = useCallback((audienceId: number, apiDerivedStatus: string) => {
    // Only reconcile when API confirms final state
    const isFinalApiStatus = ['completed', 'replied'].includes(apiDerivedStatus);
    if (!isFinalApiStatus) {
      return;
    }

    setNotifications((prev) => {
      // Find active (in-progress) notifications for this audience
      const audienceNotifications = prev.filter((n) => n.audienceId === audienceId);
      const activeNotifications = audienceNotifications.filter((n) => isInProgressNotification(n));

      // If no active notifications, nothing to reconcile
      if (activeNotifications.length === 0) {
        return prev;
      }

      appLogger.info('[reconcileWithApiStatus] Backfilling stale notifications', {
        audienceId,
        apiDerivedStatus,
        staleCount: activeNotifications.length,
        staleWorkers: activeNotifications.map(n => `${n.worker}:${n.state}`),
      });

      // Mark stale "started"/"processing" notifications as "completed"
      const updated = prev.map((notification): IWorkflowNotification => {
        if (notification.audienceId === audienceId && isInProgressNotification(notification)) {
          // Create a synthetic "completed" version of this notification
          return {
            ...notification,
            state: 'completed',
            timestamp: new Date().toISOString(),
            standardized: notification.standardized ? {
              ...notification.standardized,
              status: 'completed',
              title: notification.standardized.title?.replace(/Started|Processing|Running/i, 'Completed') || 'Completed',
              summary: `${notification.standardized.summary || notification.worker} - Reconciled from API status`,
              success_message: 'Workflow completed (synced from server)',
            } : null,
          };
        }
        return notification;
      });

      return updated;
    });

    // Also update localStorage directly to ensure persistence
    if (activeWorkspace?.vendorid) {
      const stored = loadNotificationsFromStorage(activeWorkspace.vendorid);
      const updated = stored.map((notification): IWorkflowNotification => {
        if (notification.audienceId === audienceId && isInProgressNotification(notification)) {
          return {
            ...notification,
            state: 'completed',
            timestamp: new Date().toISOString(),
            standardized: notification.standardized ? {
              ...notification.standardized,
              status: 'completed',
              title: notification.standardized.title?.replace(/Started|Processing|Running/i, 'Completed') || 'Completed',
              summary: `${notification.standardized.summary || notification.worker} - Reconciled from API status`,
              success_message: 'Workflow completed (synced from server)',
            } : null,
          };
        }
        return notification;
      });
      saveNotificationsToStorage(updated, activeWorkspace.vendorid);
    }
  }, [activeWorkspace?.vendorid]);

  const contextValue: NotificationContextType = useMemo(() => ({
    notifications,
    connectedCount,
    isConnected,
    resetCount,
    subscribeToAudience,
    unsubscribeFromAudience,
    getNotificationsForAudience,
    hasActiveNotifications,
    hasNotifications,
    clearNotificationsForAudience,
    clearAllNotifications,
    notificationsSeenAt,
    markNotificationsSeen,
    reconcileWithApiStatus,
    dialerSessions,
    getDialerSession,
    setDialerSession,
    clearDialerSession,
    subscribeToPersona,
    unsubscribeFromPersona,
    onPersonaUpdate,
    subscribeToPersonaFinder,
    unsubscribeFromPersonaFinder,
    onPersonaFinderResult,
  }), [
    notifications,
    connectedCount,
    isConnected,
    resetCount,
    subscribeToAudience,
    unsubscribeFromAudience,
    getNotificationsForAudience,
    hasActiveNotifications,
    hasNotifications,
    clearNotificationsForAudience,
    clearAllNotifications,
    notificationsSeenAt,
    markNotificationsSeen,
    reconcileWithApiStatus,
    dialerSessions,
    getDialerSession,
    setDialerSession,
    clearDialerSession,
    subscribeToPersona,
    unsubscribeFromPersona,
    onPersonaUpdate,
    subscribeToPersonaFinder,
    unsubscribeFromPersonaFinder,
    onPersonaFinderResult,
  ]);

  return (
    <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
  );
};
